// Sold out per channel — the rules the Availability page, the entry buttons and the
// register tiles share. Contract: ../API/docs/frontend/pos-product-availability.md;
// owner decisions 2026-09-12 in tasks/product-availability-decisions-api-handover.md.
import type { Product } from '../types/product'

export type AvailabilityScope = 'OWN' | 'PLATFORMS'
export type SoldOutDuration = 'END_OF_DAY' | 'UNTIL_FURTHER_NOTICE'
export type AvailabilityAction = SoldOutDuration | 'IN_STOCK'

/**
 * The page's scope switch. Owner 2026-09-12: out of stock on own systems means out of
 * stock everywhere, so there is no own-only scope — All channels writes both dimensions,
 * Platforms writes the platforms alone.
 */
export type PageScope = 'ALL' | 'PLATFORMS'

/**
 * Shows the All channels scope. Owner 2026-09-14: on — the kiosk and the webshop ship their
 * sold-out handling before this deploys. An own mark is refused at checkout on every channel
 * the moment it is published, and a kiosk without that handling turns the refusal into its
 * terminal-error screen; set to false to fall back to Platforms only.
 */
export const ALL_CHANNELS_ENABLED = true

export interface PageScopeOption {
  id: PageScope
  label: string
  subtitle: string
  scopes: AvailabilityScope[]
}

const SCOPE_OPTIONS: PageScopeOption[] = [
  {
    id: 'ALL',
    label: 'All channels',
    subtitle: 'Own systems and platforms together — own systems live on publish, platforms within a minute',
    scopes: ['OWN', 'PLATFORMS'],
  },
  {
    id: 'PLATFORMS',
    label: 'Platforms',
    subtitle: 'Uber Eats · Thuisbezorgd — sent via HubRise, platforms update within a minute',
    scopes: ['PLATFORMS'],
  },
]

export const PAGE_SCOPES: PageScopeOption[] = SCOPE_OPTIONS.filter(s => ALL_CHANNELS_ENABLED || s.id !== 'ALL')

/**
 * `products` filters for the page, the badge and the register's freshness poll — one cache
 * entry for all three. Every active product, not only display_in_pos: own systems include
 * kiosk and webshop, and a drink sold only inside meal deals must still be markable.
 * No `kind`, so the API returns base products only — meal deals are never marked (owner).
 */
export const AVAILABILITY_FILTERS = { active: true }

export const AVAILABILITY_POLL_MS = 30_000

/** One entry of `publishProductAvailability(changes:)`. */
export interface AvailabilityChange {
  product_id: number
  scopes: AvailabilityScope[]
  sold_out: boolean
  duration: SoldOutDuration | null
}

export interface SoldOutState {
  soldOut: boolean
  /** Non-null ⇔ the mark ends at the next local midnight (END_OF_DAY is the only expiry). */
  until: string | null
}

export const ownState = (p: Product): SoldOutState => ({ soldOut: !!p.sold_out, until: p.sold_out_until ?? null })
export const platformState = (p: Product): SoldOutState => ({
  soldOut: !!p.platforms_sold_out,
  until: p.platforms_sold_out_until ?? null,
})

export const isSoldOutAnywhere = (p: Product): boolean => !!p.sold_out || !!p.platforms_sold_out

export const stateLabel = (s: SoldOutState): string =>
  !s.soldOut ? 'In stock' : s.until ? 'Out of stock · back tomorrow' : 'Out of stock · until further notice'

export const actionLabel = (a: AvailabilityAction): string =>
  a === 'IN_STOCK' ? 'In stock' : a === 'END_OF_DAY' ? 'Out of stock · back tomorrow' : 'Out of stock · until further notice'

export const changeAction = (c: AvailabilityChange): AvailabilityAction =>
  !c.sold_out ? 'IN_STOCK' : c.duration === 'END_OF_DAY' ? 'END_OF_DAY' : 'UNTIL_FURTHER_NOTICE'

/** Register tile band: `SOLD OUT` + the suffix. */
export const bandSuffix = (p: Product): string => (p.sold_out_until ? '· back tomorrow' : '· until further notice')

export const buildChange = (productId: number, scopes: AvailabilityScope[], action: AvailabilityAction): AvailabilityChange => ({
  product_id: productId,
  scopes,
  sold_out: action !== 'IN_STOCK',
  duration: action === 'IN_STOCK' ? null : action,
})

const matches = (s: SoldOutState, a: AvailabilityAction): boolean =>
  a === 'IN_STOCK' ? !s.soldOut : s.soldOut && (a === 'END_OF_DAY') === (s.until !== null)

/** True when publishing the change would not alter anything in the dimensions it touches. */
export const isNoOp = (p: Product, c: AvailabilityChange): boolean => {
  const a = changeAction(c)
  return (!c.scopes.includes('OWN') || matches(ownState(p), a)) && (!c.scopes.includes('PLATFORMS') || matches(platformState(p), a))
}

/**
 * Owner 2026-09-12: while a product is out on own systems it is out everywhere, so its
 * platform state cannot be changed on its own — only through All channels.
 */
export const lockedInPlatformsScope = (p: Product): boolean => !!p.sold_out

/**
 * Option items (2026-10-27): `OptionItem.sold_out` is the linked product's live own-systems mark
 * (always false without a link). The wizards grey such an item and never let it be chosen; the
 * API refuses it at checkout on every channel. Nothing polls OptionItem entries, and the product
 * poll already carries the fact, so the wizards derive the state from it (`optionItemSoldOut`)
 * instead of polling the options query (owner 2026-10-27). The API's own field is only the
 * fallback — before the poll's first result, or for a product outside the polled set (inactive
 * products are not polled) — and it is as fresh as the options query's cache entry; the checkout
 * refusal covers what slips through.
 */
export const optionItemSoldOut = (item: { product_id?: number | null; sold_out?: boolean }, products: Map<number, boolean>): boolean => {
  const pid = item.product_id == null ? null : Number(item.product_id)
  return pid !== null && products.has(pid) ? !!products.get(pid) : !!item.sold_out
}

type OptionGroupLike = { option_group?: { option_items?: { id: number; sold_out?: boolean }[] | null } | null }

export const soldOutOptionItemIds = (groups: OptionGroupLike[]): Set<number> => {
  const ids = new Set<number>()
  groups.forEach(g => (g.option_group?.option_items || []).forEach(it => { if (it.sold_out) ids.add(Number(it.id)) }))
  return ids
}

/** The selection without the sold-out items — the same object when nothing changed. */
export const withoutSoldOutOptions = (selected: Record<number, number[]>, soldOut: Set<number>): Record<number, number[]> => {
  if (soldOut.size === 0) return selected
  let changed = false
  const next: Record<number, number[]> = {}
  for (const [gid, ids] of Object.entries(selected)) {
    const kept = ids.filter(id => !soldOut.has(Number(id)))
    if (kept.length !== ids.length) changed = true
    next[Number(gid)] = kept
  }
  return changed ? next : selected
}
